import { useEffect, useState, useCallback } from 'react';

// const WEB_INTERSTITIAL_UNIT = '/6355419/Travel/Europe/France/Paris';
const WEB_INTERSTITIAL_UNIT = '/23198695442/kisan_app/Homepage_Interstitial';

/** Wait until googletag is ready */
const waitForGoogletagReady = (timeout = 5000): Promise<typeof window.googletag> => {
  return new Promise((resolve, reject) => {
    const interval = 100;
    let waited = 0;
    const check = () => {
      if (window?.googletag && window.googletag?.cmd) return resolve(window.googletag);
      if ((waited += interval) >= timeout) return reject('GPT script not ready in time');
      setTimeout(check, interval);
    };
    check();
  });
};

/** Inject GPT script dynamically */
const injectGPTScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.querySelector('#gpt-script')) return resolve();

    const script = document.createElement('script');
    script.id = 'gpt-script';
    script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
};

export const useInterstitialAds = (options?: { adUnitId?: string }) => {
  const adUnitId = options?.adUnitId ?? WEB_INTERSTITIAL_UNIT;
  const [slot, setSlot] = useState<googletag.Slot | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [isAdClosed, setIsAdClosed] = useState(false);
  const [isError, setIsError] = useState(false);

  // const adUnitId = WEB_INTERSTITIAL_UNIT;

  const getOutOfPageSlotByAdUnitPath = (adUnitPath: string) => {
    if (typeof window === 'undefined' || !window.googletag?.pubads) return [];

    const allSlots = window.googletag.pubads().getSlots();

    return allSlots.filter(
      (slot: any) =>
        (!slot.getSlotElementId() || slot.getSlotElementId() === '') &&
        slot.getAdUnitPath() === adUnitPath
    );
  };

  /** ✅ Load and display interstitial */
  const loadAd = useCallback(async () => {
    try {
      setLoaded(false);
      setIsError(false);

      await injectGPTScript();
      const interstitials = getOutOfPageSlotByAdUnitPath(adUnitId);
      if (interstitials?.length) googletag.destroySlots(interstitials);

      googletag.cmd.push(() => {
        const interstitialSlot = googletag.defineOutOfPageSlot(
          adUnitId,
          googletag.enums.OutOfPageFormat.INTERSTITIAL
        );

        if (!interstitialSlot) {
          // console.warn('❌ Failed to define interstitial slot');
          setIsError(true);
          return;
        }

        interstitialSlot.addService(googletag.pubads());
        setSlot(interstitialSlot);

        googletag.pubads().enableSingleRequest();
        googletag.enableServices();

        googletag.pubads().addEventListener('slotRenderEnded', (event) => {
          if (event.slot === interstitialSlot) {
            // console.log('✅ Interstitial ad rendered!');
            setLoaded(true);
            setIsAdClosed(false);
          }
        });

        googletag.pubads().addEventListener('impressionViewable', (event) => {
          if (event.slot === interstitialSlot) {
            // console.log('👀 Interstitial viewable!');
          }
        });

        googletag.pubads().addEventListener('slotVisibilityChanged', (event) => {
          if (event.slot === interstitialSlot && event.inViewPercentage === 0) {
            // console.log('🧭 Interstitial closed');
            setLoaded(false);
            setIsAdClosed(true);
            setTimeout(() => loadAd(), 2000); // Preload next ad
          }
        });

        // ✅ TypeScript-safe & runtime-correct way
        // googletag.display(interstitialSlot);
      });
    } catch (err) {
      console.error('❌ Failed to load interstitial ad:', err);
      setLoaded(false);
      setIsError(true);
    }
  }, [adUnitId]);

  /** ✅ Show ad manually */
  const showAd = useCallback(() => {
    if (loaded && slot) {
      // console.log('🎬 Interstitial already loaded — ready to show.');
      // Interstitial auto-shows on load
      googletag.display(slot);
    } else {
      console.log('⚠️ Ad not ready — loading now...');
      loadAd();
    }
  }, [loaded, loadAd, slot]);

  useEffect(() => {
    loadAd();
  }, [loadAd]);

  return { showAd, isAdClosed, loaded, isError };
};
